/**
 * Session-only demo state for Reports: saved reports and their exports,
 * seeded from the tenant fixture and mutated by the session's own
 * create/duplicate/schedule/export actions — like `AutomationStore` for
 * Automation. Nothing here runs a real export pipeline: a request resolves
 * immediately, because there is no engine to poll for progress.
 */

import {
  REPORT_DATASET_COLUMNS,
  REPORT_DATASET_LABELS,
  REPORT_DATASETS,
  type ReportDataset,
  type ReportExport,
  type ReportFormat,
  type ReportInput,
  type ReportSchedule,
  type SavedReport,
} from '@/api/reports'
import { addDays } from '@/lib/filters'

import type { TenantFixture } from '../tenants/types'

/** How long a generated export's placeholder link is treated as live before it shows as expired. */
const EXPORT_TTL_DAYS = 7

export class UnknownReportError extends Error {
  constructor(id: string) {
    super(`Unknown report: ${id}`)
    this.name = 'UnknownReportError'
  }
}

function seedReports(tenant: TenantFixture, today: string): SavedReport[] {
  return REPORT_DATASETS.map((dataset, index) => ({
    id: `rpt_${dataset}`,
    name: `${REPORT_DATASET_LABELS[dataset]} report`,
    dataset,
    columns: REPORT_DATASET_COLUMNS[dataset].map((column) => column.key),
    owner: tenant.user.name,
    createdAt: addDays(today, -(45 + index * 12)),
    updatedAt: addDays(today, -(index * 4)),
    schedule:
      index === 0
        ? { cadence: 'weekly', recipients: [tenant.user.email], format: 'csv', nextRunAt: addDays(today, 7) }
        : null,
  }))
}

function seedExports(
  tenant: TenantFixture,
  reports: readonly SavedReport[],
  today: string,
  nextId: (prefix: string) => string,
): ReportExport[] {
  const [first, second] = reports
  const entries: ReportExport[] = []
  if (first) {
    const requestedAt = addDays(today, -1)
    entries.push({
      id: nextId('exp'),
      reportId: first.id,
      reportName: first.name,
      dataset: first.dataset,
      format: 'csv',
      requestedAt,
      requestedBy: tenant.user.name,
      state: 'ready',
      expiresAt: addDays(requestedAt, EXPORT_TTL_DAYS),
      downloadHint: `${first.dataset}-${requestedAt}.csv`,
    })
  }
  if (second) {
    const requestedAt = addDays(today, -20)
    entries.push({
      id: nextId('exp'),
      reportId: second.id,
      reportName: second.name,
      dataset: second.dataset,
      format: 'csv',
      requestedAt,
      requestedBy: tenant.user.name,
      state: 'expired',
      expiresAt: addDays(requestedAt, EXPORT_TTL_DAYS),
      downloadHint: `${second.dataset}-${requestedAt}.csv`,
    })
  }
  return entries
}

export class ReportsStore {
  private reports: SavedReport[]
  private exportEntries: ReportExport[]
  /** Instance-scoped: one tenant's ids never depend on another tenant's store having been created first. */
  private idCounter = 0

  constructor(
    private readonly tenant: TenantFixture,
    today: string,
  ) {
    this.reports = seedReports(tenant, today)
    this.exportEntries = seedExports(tenant, this.reports, today, (prefix) => this.nextId(prefix))
  }

  private nextId(prefix: string): string {
    this.idCounter += 1
    return `${prefix}_${this.idCounter}`
  }

  list(): SavedReport[] {
    return [...this.reports].sort((a, b) => a.name.localeCompare(b.name))
  }

  get(id: string): SavedReport | null {
    return this.reports.find((report) => report.id === id) ?? null
  }

  create(input: ReportInput, today: string): SavedReport {
    const report: SavedReport = {
      id: this.nextId('rpt'),
      name: input.name,
      dataset: input.dataset,
      columns: input.columns,
      owner: this.tenant.user.name,
      createdAt: today,
      updatedAt: today,
      schedule: null,
    }
    this.reports.push(report)
    return report
  }

  duplicate(id: string, today: string): SavedReport {
    const source = this.get(id)
    if (!source) throw new UnknownReportError(id)
    const copy: SavedReport = { ...source, id: this.nextId('rpt'), name: `${source.name} (copy)`, createdAt: today, updatedAt: today, schedule: null }
    this.reports.push(copy)
    return copy
  }

  /** Returns a new report object rather than mutating the stored one, so a caller already holding a reference from `get`/`list` never sees it change under it. */
  schedule(id: string, schedule: ReportSchedule | null, today: string): SavedReport {
    const index = this.reports.findIndex((report) => report.id === id)
    if (index === -1) throw new UnknownReportError(id)
    const updated: SavedReport = { ...(this.reports[index] as SavedReport), schedule, updatedAt: today }
    this.reports[index] = updated
    return updated
  }

  exports(): ReportExport[] {
    return [...this.exportEntries].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt) || b.id.localeCompare(a.id))
  }

  requestExport(reportId: string | null, dataset: ReportDataset, format: ReportFormat, today: string): ReportExport {
    const reportName = (reportId ? this.get(reportId)?.name : undefined) ?? REPORT_DATASET_LABELS[dataset]
    const entry: ReportExport = {
      id: this.nextId('exp'),
      reportId,
      reportName,
      dataset,
      format,
      requestedAt: today,
      requestedBy: this.tenant.user.name,
      // No export pipeline exists in the demo, so a request resolves immediately.
      state: 'ready',
      expiresAt: addDays(today, EXPORT_TTL_DAYS),
      downloadHint: `${dataset}-${today}.${format}`,
    }
    this.exportEntries.push(entry)
    return entry
  }
}
